import React, { CSSProperties, ReactNode, useState } from 'react';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import { GroupSortInput, useGetGroupListQuery } from '../../../generated/graphql';
import DeleteGroupsDialog from '../../../components/dialogs/DeleteGroupsDialog/DeleteGroupsDialog';
import NewGroupDialog from '../../../components/dialogs/NewGroupDialog/NewGroupDialog';
import { Plus, Trash2 } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';

type GroupsProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    className?: string;
};

const Groups = (props: GroupsProps) => {
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [deleteGroupDialogVisible, setDeleteGroupsDialogVisible] = useState(false);
    const [newGroupVisible, setNewGroupVisible] = useState(false);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    const result = useGetGroupListQuery({
        variables: {
            contains: searchText,
            order: createGraphQLOrder<GroupSortInput>(sort),
        }
    });

    const handleNewGroupOnClose = () => {
        setNewGroupVisible(false);
        result.refetch();
    };
    const handleDeleteGroupOnClose = () => {
        setDeleteGroupsDialogVisible(false);
        result.refetch();
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <button className="btn btn-ghost text-sm" onClick={() => setNewGroupVisible(true)}>
                    <Plus size={15} /> Add
                </button>
                <RefreshButton onRefresh={() => result.refetch()} />
                {selectedIds.length > 0 && (
                    <>
                        <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                        <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteGroupsDialogVisible(true)}>
                            <Trash2 size={15} /> Delete
                        </button>
                    </>
                )}
            </div>

            <div className="flex flex-col flex-1 overflow-hidden">
                <DataTableNew
                    showDescription
                    isLoading={result.loading}
                    data={result.data?.GroupList?.nodes}
                    onSelected={(ids) => setSelectedIds(ids)}
                    onSearchChanged={(value) => setSearchText(value)}
                    sortingMode="server"
                    sort={sort}
                    onSortChange={setSort}
                    fetchMore={() => result.data?.GroupList?.pageInfo?.hasNextPage && result.fetchMore({
                        variables: {
                            contains: searchText,
                            after: result.data?.GroupList?.pageInfo.endCursor,
                            order: createGraphQLOrder<GroupSortInput>(sort),
                        },
                        updateQuery: (previousResult, { fetchMoreResult }) => {
                            if (!fetchMoreResult) return previousResult;
                            const previousContents = result.data?.GroupList?.nodes || [];
                            const newContents = fetchMoreResult.GroupList?.nodes || [];
                            return {
                                ...previousResult,
                                GroupList: {
                                    nodes: [...previousContents, ...newContents],
                                    pageInfo: fetchMoreResult.GroupList?.pageInfo as any
                                }
                            };
                        }
                    })}
                />
            </div>

            <DeleteGroupsDialog ids={selectedIds} visible={deleteGroupDialogVisible} onClose={handleDeleteGroupOnClose} />
            <NewGroupDialog visible={newGroupVisible} onClose={handleNewGroupOnClose} />
        </div>
    );
};

export default Groups;
